import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, ShoppingCart, Package, FileText, Users,
  BarChart3, Settings, LogOut, Menu, X, ChevronDown, Bell,
  Store, Truck, Printer, HelpCircle, Boxes, Wallet, MessageSquare,
  Mail, FileCode, Shield, Lock, BookOpen, CreditCard, Globe, Megaphone
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import NotificationBell from '@/components/shop/NotificationBell';
import TourGuide from '@/components/shop/TourGuide';

const ShopLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, setLanguage, availableLanguages } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showTourGuide, setShowTourGuide] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const currentLang = availableLanguages.find(l => l.code === language);

  const menuItems = [
    { name: t('shop_nav_dashboard'), icon: LayoutDashboard, path: '/shop/dashboard' },
    { name: 'Help & Docs', icon: HelpCircle, path: '/shop/help' },
    { name: t('shop_nav_pos'), icon: ShoppingCart, path: '/shop/pos' },
    { name: t('shop_nav_products'), icon: Package, path: '/shop/products' },
    { name: t('shop_nav_stock'), icon: Boxes, path: '/shop/stock' },
    { name: t('shop_nav_invoices'), icon: FileText, path: '/shop/invoices' },
    { name: t('shop_nav_staff'), icon: Users, path: '/shop/staff' },
    { name: t('shop_nav_reports'), icon: BarChart3, path: '/shop/reports' },
    { name: t('shop_nav_delivery'), icon: Truck, path: '/shop/delivery' },
    { name: t('shop_nav_printer'), icon: Printer, path: '/shop/print-settings' },
    { name: t('shop_nav_payments'), icon: Wallet, path: '/shop/payment-settings' },
    { name: t('shop_nav_tickets'), icon: MessageSquare, path: '/shop/tickets' },
    { name: t('shop_nav_email_templates'), icon: FileCode, path: '/shop/email-templates' },
    { name: t('shop_nav_smtp'), icon: Mail, path: '/shop/smtp-settings' },
    { name: 'VeriFactu', icon: Shield, path: '/shop/verifactu' },
    { name: 'Social Posts', icon: Megaphone, path: '/shop/social' },
    { name: t('shop_nav_settings'), icon: Settings, path: '/shop/settings' },
    { name: 'Subscription', icon: CreditCard, path: '/shop/subscription' },
  ];

  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path || (path !== '/shop/dashboard' && location.pathname.startsWith(path + '/'));

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="shop-layout">
      {/* Sidebar - Desktop */}
      <aside className={`fixed inset-y-0 left-0 z-50 bg-[#1e3a5f] transition-all duration-300 hidden lg:flex lg:flex-col ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/shop/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.png" alt="VendFactura" className="h-14 w-auto object-contain" />
            </div>
            {sidebarOpen && <span className="text-white font-bold text-lg sr-only">VendFactura</span>}
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/60 hover:text-white p-1">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {sidebarOpen && (
          <div className="px-4 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.shop_name || 'Mi Tienda'}</p>
                <p className="text-white/50 text-xs truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                isActive(item.path) ? 'bg-[#22c55e] text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              title={!sidebarOpen ? item.name : undefined}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="truncate text-sm">{item.name}</span>}
            </Link>
          ))}
        </nav>

        <div className="flex-shrink-0 px-3 py-3 border-t border-white/10 space-y-1">
          <Link to="/shop/help" className="flex items-center gap-3 px-3 py-2.5 text-white/70 hover:text-white hover:bg-white/10 rounded-sm transition-colors">
            <HelpCircle className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">{t('shop_nav_help')}</span>}
          </Link>
          <button onClick={() => setShowTourGuide(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-[#22c55e] hover:text-[#16a34a] hover:bg-white/10 rounded-sm transition-colors"
            title={!sidebarOpen ? 'Tour Guide' : undefined}
            data-testid="tour-guide-btn"
          >
            <BookOpen className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Tour Guide</span>}
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-white/70 hover:text-red-400 hover:bg-white/10 rounded-sm transition-colors">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">{t('shop_nav_logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#1e3a5f] z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileMenuOpen(true)} className="text-white p-2">
          <Menu className="w-6 h-6" />
        </button>
        <Link to="/shop/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="V" className="h-10 w-auto object-contain" />
          </div>
        </Link>
        <button className="text-white p-2 relative">
          <Bell className="w-6 h-6" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        </button>
      </header>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setMobileMenuOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 bg-[#1e3a5f] z-50">
              <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
                <img src="/logo.png" alt="VendFactura" className="h-10 w-auto object-contain" />
                <button onClick={() => setMobileMenuOpen(false)} className="text-white p-2">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="p-4 space-y-1">
                {menuItems.map((item) => (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                      isActive(item.path) ? 'bg-[#22c55e] text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}>
                    <item.icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </nav>
              <div className="p-4 border-t border-white/10">
                <button onClick={() => { setShowTourGuide(true); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-[#22c55e] hover:bg-white/10 transition-colors"
                  data-testid="tour-guide-btn-mobile"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Tour Guide</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} pt-16 lg:pt-0`}>
        <header className="hidden lg:flex h-16 bg-white border-b border-slate-200 items-center justify-between px-6">
          <div>
            <h1 className="text-lg font-semibold text-[#1e3a5f]">
              {menuItems.find(item => isActive(item.path))?.name || 
               (location.pathname.includes('verifactu') ? 'VeriFactu' : t('shop_nav_dashboard'))}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />

            {/* Language Dropdown */}
            <div className="relative">
              <button onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm hover:bg-slate-100 border border-slate-200 text-sm"
                data-testid="dash-lang-btn">
                <Globe className="w-4 h-4 text-slate-500" />
                <span className="text-slate-700 font-medium">{currentLang?.flag} {currentLang?.code?.toUpperCase()}</span>
              </button>
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                    className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 shadow-lg py-1 z-50"
                    data-testid="dash-lang-menu">
                    {availableLanguages.map(lang => (
                      <button key={lang.code}
                        onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 ${
                          language === lang.code ? 'bg-green-50 text-[#22c55e] font-medium' : 'text-slate-700'
                        }`}
                        data-testid={`dash-lang-${lang.code}`}>
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="relative">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-slate-100">
                <div className="w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                <span className="text-sm font-medium text-slate-700">{user?.name || 'User'}</span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 shadow-lg py-2">
                    <Link to="/shop/settings" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                      onClick={() => setUserMenuOpen(false)}>{t('shop_nav_settings')}</Link>
                    <button onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-100">
                      {t('shop_nav_logout')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>
        <div className="p-6 pb-20 lg:pb-6">{children}</div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16">
          {[
            { name: t('shop_nav_dashboard'), icon: LayoutDashboard, path: '/shop/dashboard' },
            { name: t('shop_nav_pos'), icon: ShoppingCart, path: '/shop/pos' },
            { name: t('shop_nav_invoices'), icon: FileText, path: '/shop/invoices' },
            { name: t('shop_nav_reports'), icon: BarChart3, path: '/shop/reports' },
            { name: t('shop_nav_tickets'), icon: MessageSquare, path: '/shop/tickets' },
          ].map(item => (
            <Link key={item.path} to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive(item.path) ? 'text-[#22c55e]' : 'text-slate-400'
              }`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 font-medium">{item.name}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Tour Guide Modal */}
      {showTourGuide && <TourGuide onClose={() => setShowTourGuide(false)} />}
    </div>
  );
};

export default ShopLayout;
